import React, { type ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth';
import { subscribeSystemConfig } from '../../api/firebase/firestore/system/read';
import { isOperatorStaff } from '../../utils/permission';
import Maintenance from '../pages/common/Maintenance';
import CustomLoading from '../uiParts/form/CustomLoading';

// メンテナンス中でも常にアクセスを許可する公開ルート。
// 未ログインの運営管理者がログインしてバイパスできるよう、
// ログイン・認証（パスワード/登録/メールアクション）系は除外対象とする。
const PUBLIC_PATHS = ['/', '/action'];
const PUBLIC_PATH_PREFIXES = ['/auth/'];

const isPublicPath = (pathname: string): boolean =>
  PUBLIC_PATHS.includes(pathname) ||
  PUBLIC_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));

/**
 * メンテナンス画面のゲート。
 * Firestore の system/config をリアルタイム監視し、
 * メンテナンス有効時は通常画面の代わりにメンテナンス画面を表示する。
 * - 運営スタッフ（admin / operator）は動作確認のためバイパスして通常画面にアクセスできる
 *   （firestore.rules の isStaff と整合させ、フラグを操作できる運営は締め出さない）。
 * - ログイン・認証系の公開ルートはメンテナンス中も常に表示する
 *   （未ログインの運営スタッフがログインしてバイパスするための導線を確保するため）。
 */
const MaintenanceGate = ({ children }: { children: ReactNode }): JSX.Element => {
  const { user, isLoading } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | undefined>(undefined);
  // メンテ設定の初回スナップショットを受信済みか。
  // 受信前は maintenanceEnabled の初期値 false が「メンテ無効」と区別できないため、
  // 保護ルートの判定を保留するために用いる。
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeSystemConfig(config => {
      setMaintenanceEnabled(config.maintenance_enabled);
      setMaintenanceMessage(config.maintenance_message);
      setConfigLoaded(true);
    });
    return unsubscribe;
  }, []);

  const isProtectedPath = !isPublicPath(pathname);

  // 保護ルートでは、メンテ状態が確定するまで children を描画しない。
  // 確定前（認証解決前 or メンテ設定取得前）に children を出すと、
  // 保護ページ側の未認証リダイレクトが先に / (公開パス) へ遷移してしまい、
  // メンテ画面に到達できなくなる競合を防ぐ（#2879）。
  // 認証解決を待つことで、運営スタッフへメンテ画面を一瞬見せることも防ぐ。
  if (isProtectedPath && (isLoading || !configLoaded)) {
    return <CustomLoading loading />;
  }

  // メンテナンス画面を表示する条件:
  // - メンテナンス有効
  // - 公開（ログイン・認証）ルートではない
  // - 運営スタッフではない（運営スタッフはバイパス）
  const shouldShowMaintenance =
    maintenanceEnabled && isProtectedPath && !isOperatorStaff(user?.role);

  if (shouldShowMaintenance) {
    return <Maintenance message={maintenanceMessage} />;
  }

  return <>{children}</>;
};

export default MaintenanceGate;
